import { useState, useRef } from "react";
import { Backdrop, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormLabel, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useDispatch, useSelector } from 'react-redux';
import { Editor } from 'tinymce';
import { Button } from "rsuite";
import { toast } from "react-toastify";

import { addNewProject } from "../../services/openProject.service";
import TinyMCEEditor from "../tinymce";
import validationSchema, { initialValues } from "./Validator";
import { SearchableDropDown } from "../SearchableDropDown/index";
import { RootState } from "../../store/store";
import { setNewProjectCreated, } from "../../store/projectSlice";
import { ProjectList } from "../../common/interface";
import "./style.scss";

interface NewProjectProps {
    projects: ProjectList[];
    newProjectVisibility: boolean;
    setNewProjectVisibility: () => void;
};

const NewProject = ({ projects, newProjectVisibility, setNewProjectVisibility }: NewProjectProps) => {
    const [selectedSubProject, setSelectedSubProject] = useState<ProjectList | null>(null);
    const [fullScreenLoading, setFullScreenLoading] = useState(false);
    const editorRef = useRef<Editor | null>(null);
    const subProjectInputRef = useRef<HTMLInputElement>(null);

    const projectState = useSelector((state: RootState) => state.project);
    const userState = useSelector((state: RootState) => state.user);
    const isNewProjectCreated = projectState.newProjectCreated;
    const dispatch = useDispatch();

    const onSubmit = async (values: typeof initialValues) => {
        try {
            const userDetails = userState?.user;
            const openProjectUserId = userDetails?.openProjectDetails?.id;
            setFullScreenLoading(true);
            const { projectName, isPublic } = values;

            if (selectedSubProject === null) {
                subProjectInputRef.current?.classList.add("subproject-invalid");
                if (!document.getElementById("subproject-error")) {
                    const errorMessage = document.createElement("p");
                    errorMessage.id = "subproject-error";
                    errorMessage.textContent = "Subproject is required";
                    errorMessage.className = "error-message";
                    subProjectInputRef.current?.parentNode?.appendChild(errorMessage);
                }
                return;
            }

            const subProjectLink = selectedSubProject?.projectLink || "";

            const description = editorRef.current ? editorRef.current.getContent() : "";

            const data = {
                "name": projectName,
                "description": description,
                "public": isPublic,
                "subProjectOf": subProjectLink,
                "openProjectUserId": openProjectUserId
            };

            const response = await addNewProject(data);
            setNewProjectVisibility();
            if (response?.statusCode === 200) {
                toast.success("Project created successfully");
                formik.resetForm();
                dispatch(setNewProjectCreated(!isNewProjectCreated));
            } else {
                toast.error("Project Creation Failed");
            }
        } catch (error) {
            toast.error("Project Creation Failed");
            setNewProjectVisibility();
        } finally {
            setFullScreenLoading(false);
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleSubProjectSelection = (subProjectOf: ProjectList | null) => {
        setSelectedSubProject(subProjectOf);
    }

    const handleProjectClose = () => {
        formik.resetForm();
        setNewProjectVisibility();
    };

    return (
        <>
            <Dialog open={newProjectVisibility} className="create-openproject-task-dialog-container">
                <DialogTitle>New Project</DialogTitle>
                <DialogContent className="create-openproject-sub-task-dialog-content">
                    <form className="dialog-openproject-form" onSubmit={formik.handleSubmit}>
                        <FormLabel htmlFor="projectName">Project Name*</FormLabel>
                        <TextField
                            id="projectName"
                            required
                            type="text"
                            name="projectName"
                            value={formik.values.projectName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.projectName && Boolean(formik.errors.projectName)}
                            helperText={formik.touched.projectName && formik.errors.projectName}
                            fullWidth
                        />
                        <FormLabel htmlFor="subproject">Subproject of*</FormLabel>
                        <SearchableDropDown
                            projects={projects}
                            selectedSubProject={selectedSubProject}
                            handleSubProjectSelection={handleSubProjectSelection}
                            subProjectInputRef={subProjectInputRef}
                        />
                        <FormLabel>Project Description</FormLabel>
                        <TinyMCEEditor editorRef={editorRef} />
                        <FormControlLabel style={{width: "fit-content"}}
                            control={<Checkbox name="isPublic" checked={formik.values.isPublic} onChange={formik.handleChange} onBlur={formik.handleBlur} />}
                            label="Public"
                        />
                        <DialogActions className="new_project_actions">
                            <Button className="cancel_btn" onClick={handleProjectClose}>Cancel</Button>
                            <Button className="create_btn" type="submit">Submit</Button>
                        </DialogActions>
                    </form>
                    <Backdrop open={fullScreenLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                        <CircularProgress />
                    </Backdrop>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default NewProject;